import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	TabList,
	TabPanel,
	TabPanels,
	Tabs,
	VisuallyHidden,
	VStack,
} from '@bonusly/design-system';

import { HighlightTab, HighlightTabDropdown, HighlightTabOption, HighlightTabOptions, HighlightTabTrigger } from '../../components/common/tabs';
import { useReactionTabs } from './hooks/use-reaction-tabs';
import { useResponsiveTabs } from './hooks/use-responsive-tabs';
import { ReactionCard } from './reaction-card';
import { useReactionContext } from './reaction-detail-context';
import { modalContentStyle } from './styles';
import { TabCount } from './tab-count';

export const ReactionModalContent: FC = () => {
	const { t } = useTranslation(['common', 'recognition']);
	const { bonus } = useReactionContext();
	const { onSelectTab, selectedTab, tabs } = useReactionTabs(bonus);
	const { displayedTabs, overflowTabs, tabSize } = useResponsiveTabs(tabs);

	return (
		<Tabs index={selectedTab} onChange={onSelectTab}>
			<ModalContent {...modalContentStyle}>
				<ModalHeader borderBottom="none" pb={0}>
					{t('reactions.modal.header', { ns: 'recognition' })}

					<TabList
						border="none"
						flexWrap={{ base: 'nowrap', xs: 'wrap' }}
						overflowX={{ base: 'auto', xs: 'visible' }}
						pos="relative"
						zIndex={1}
						css={{
							'&::-webkit-scrollbar': {
								display: 'none',
							},
						}}
					>
						{displayedTabs.map((tab) => {
							return (
								<HighlightTab key={tab.type} px={4} w={tabSize}>
									<TabCount tab={tab} />
								</HighlightTab>
							);
						})}

						{!!overflowTabs.length && (
							<HighlightTabDropdown indexOffset={displayedTabs.length}>
								<HighlightTabTrigger w={tabSize}>{t('more')}</HighlightTabTrigger>

								<HighlightTabOptions
									aria-label={t('reactions.modal.dropdown_label', {
										ns: 'recognition',
									})}
								>
									{overflowTabs.map((tab) => {
										return (
											<HighlightTabOption key={tab.type}>
												<TabCount tab={tab} w="fit-content" />
												<VisuallyHidden>
													{t('reactions.modal.dropdown_option', {
														ns: 'recognition',
														type: tab.type,
													})}
												</VisuallyHidden>
											</HighlightTabOption>
										);
									})}
								</HighlightTabOptions>
							</HighlightTabDropdown>
						)}
					</TabList>

					<Box background="gray.50" mb="-2px" height={0.5} pos="relative" transform="translate(0, -2px)" zIndex={0} />
				</ModalHeader>

				<ModalCloseButton borderRadius="full" />

				<ModalBody pt={4}>
					<TabPanels>
						{tabs.map((tab) => {
							return (
								<TabPanel key={tab.type} p={0} pb={4}>
									<VStack spacing={4}>
										{tab.reactions.map((reaction) => {
											return <ReactionCard key={reaction.id} reaction={reaction} alignSelf="stretch" />;
										})}
									</VStack>
								</TabPanel>
							);
						})}
					</TabPanels>
				</ModalBody>
			</ModalContent>
		</Tabs>
	);
};
